/**
 * Location hierarchy utilities for sorting items by proximity tiers:
 * same area → same LGA → same state → other, then by distance within each tier.
 *
 * Used by Home (LocationBasedItems) and Market to show "Ogbomosho Central first,
 * then rest of Ogbomosho, then rest of Oyo, then other states".
 */

import type { LocationData } from "@/lib/locationUtils";
import {
  LAGOS_LGAS,
  OYO_LGAS,
  LAGOS_NEIGHBORHOODS,
  OYO_NEIGHBORHOODS,
  parseLocationString,
} from "@/lib/locationUtils";

/** Minimal user location shape (avoids importing from LocationContext here). */
export interface UserLocationForHierarchy {
  areaName: string;
  formattedAddress?: string;
}

export type LocationTier = "area" | "lga" | "state" | "other";

/** Order for sorting: area first, then lga, then state, then other. */
export const LOCATION_TIER_ORDER: Record<LocationTier, number> = {
  area: 0,
  lga: 1,
  state: 2,
  other: 3,
};

const LAGOS_STATE_NAME = "Lagos";
const OYO_STATE_NAME = "Oyo";

/** LGAs keyed by id for lookup. */
const ALL_LGAS: LocationData[] = [...LAGOS_LGAS, ...OYO_LGAS];
const ALL_NEIGHBORHOODS: LocationData[] = [
  ...LAGOS_NEIGHBORHOODS,
  ...OYO_NEIGHBORHOODS,
];

/**
 * Derive user's state and LGA from their location (areaName + optional formattedAddress).
 * Uses neighborhood/LGA reference data when areaName matches a known neighborhood.
 */
export function getUserStateAndLGA(userLocation: UserLocationForHierarchy): {
  state: string;
  lga: string | null;
} {
  const areaLower = userLocation.areaName?.trim().toLowerCase() ?? "";
  const formatted = userLocation.formattedAddress?.trim() ?? "";

  // 1. Look up areaName in known neighborhoods to get LGA and state
  const neighborhood = ALL_NEIGHBORHOODS.find(
    (n) => n.name.toLowerCase() === areaLower,
  );
  if (neighborhood?.parent) {
    const lga = ALL_LGAS.find((l) => l.id === neighborhood.parent);
    if (lga) {
      const state =
        lga.parent === "lagos_state"
          ? LAGOS_STATE_NAME
          : lga.parent === "oyo_state"
            ? OYO_STATE_NAME
            : lga.name;
      return { state, lga: lga.name };
    }
  }

  // 2. Try parsing formattedAddress (e.g. "Ogbomosho Central, Ogbomosho North, Oyo, Nigeria")
  if (formatted) {
    const parsed = parseLocationString(formatted);
    if (parsed.state) {
      const state = parsed.state.toLowerCase().includes("lagos")
        ? LAGOS_STATE_NAME
        : parsed.state.toLowerCase().includes("oyo")
          ? OYO_STATE_NAME
          : parsed.state;
      return { state, lga: parsed.lga ?? null };
    }
  }

  // 3. Infer state from areaName
  if (
    areaLower.includes("lagos") ||
    areaLower.includes("ikeja") ||
    areaLower.includes("lekki")
  ) {
    return { state: LAGOS_STATE_NAME, lga: null };
  }
  if (
    areaLower.includes("oyo") ||
    areaLower.includes("ibadan") ||
    areaLower.includes("ogbomosho") ||
    areaLower.includes("saki")
  ) {
    return { state: OYO_STATE_NAME, lga: null };
  }

  return { state: LAGOS_STATE_NAME, lga: null };
}

/**
 * Single owner for "which location-string substrings identify a state".
 *
 * This curated per-state keyword list is intentionally SMALL (state name + major
 * city/LGA aliases) so it can drive BOTH client-side tier classification
 * (`getStateFromLocationString`) and the server-side in-state fetch filter
 * (`getStateLocationKeywords` → ILIKE OR in `itemsAPI`). Keep it bounded — a
 * server `.or()` built from thousands of neighborhood names would be unusably slow.
 */
const STATE_LOCATION_KEYWORDS: Record<string, string[]> = {
  [LAGOS_STATE_NAME]: [
    "lagos",
    "lekki",
    "ikeja",
    "ajah",
    "yaba",
    "surulere",
    "shomolu",
    "agege",
    "ikorodu",
    "badagry",
    "epe",
  ],
  [OYO_STATE_NAME]: [
    "oyo",
    "ibadan",
    "ogbomosho",
    "ogbomoso",
    "saki",
    "iseyin",
  ],
};

/**
 * Canonical location-string keywords that indicate an item is in `state`.
 * Returns [] for unknown states (callers should then fall back to no filter).
 * Case-insensitive on the state name.
 */
export function getStateLocationKeywords(state: string): string[] {
  const match = Object.keys(STATE_LOCATION_KEYWORDS).find(
    (key) => key.toLowerCase() === state.trim().toLowerCase(),
  );
  return match ? STATE_LOCATION_KEYWORDS[match] : [];
}

/**
 * Infer state from an item's location string (e.g. "Ibadan", "Lekki, Lagos").
 * Uses the shared {@link STATE_LOCATION_KEYWORDS} owner so client tiering and the
 * server in-state fetch stay in agreement.
 */
function getStateFromLocationString(locationString: string): string | null {
  const loc = locationString.trim().toLowerCase();
  if (!loc) return null;
  for (const [state, keywords] of Object.entries(STATE_LOCATION_KEYWORDS)) {
    if (keywords.some((keyword) => loc.includes(keyword))) {
      return state;
    }
  }
  return null;
}

/**
 * Classify an item's location into a tier relative to the user:
 * "area" = same area (e.g. Ogbomosho Central),
 * "lga" = same LGA but not same area,
 * "state" = same state but not same LGA,
 * "other" = different state.
 */
/**
 * Order items by proximity TIER only (area → lga → state → other), newest first
 * within each tier. This is intentionally **distance-independent** so callers can
 * render immediately in a sensible order and refine within-tier ordering once the
 * (async) distance-matrix results arrive — no blocking on the network round-trip.
 */
export function orderItemsByTier<
  T extends { location?: string; createdAt: Date | string },
>(items: T[], userLocation: UserLocationForHierarchy): T[] {
  const { state, lga } = getUserStateAndLGA(userLocation);
  const areaName = userLocation.areaName ?? "";

  const byTier: Record<LocationTier, T[]> = {
    area: [],
    lga: [],
    state: [],
    other: [],
  };
  for (const item of items) {
    const tier = getLocationTier(item.location ?? "", areaName, state, lga);
    byTier[tier].push(item);
  }

  const toMs = (value: Date | string): number =>
    value instanceof Date ? value.getTime() : new Date(value).getTime();
  (["area", "lga", "state", "other"] as const).forEach((tier) => {
    byTier[tier].sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt));
  });

  return [...byTier.area, ...byTier.lga, ...byTier.state, ...byTier.other];
}

export function getLocationTier(
  itemLocation: string,
  userAreaName: string,
  userState: string,
  userLGA: string | null,
): LocationTier {
  const itemLoc = itemLocation?.trim().toLowerCase() ?? "";
  const area = userAreaName?.trim().toLowerCase() ?? "";
  const state = userState?.trim().toLowerCase() ?? "";
  const lga = userLGA?.trim().toLowerCase() ?? "";

  if (!itemLoc) return "other";

  if (area && itemLoc.includes(area)) return "area";
  if (lga && itemLoc.includes(lga)) return "lga";

  const itemState = getStateFromLocationString(itemLocation);
  if (itemState && itemState.toLowerCase() === state) return "state";

  return "other";
}
